// Cho'Gath - Rupture: targets the most-clustered enemy group, marks the area
// for 1 second, then ruptures the ground dealing magic damage and knocking up all enemies hit.

import { magicDamage } from "../../combat/DamageCalc";
import { hexDistance, hexesWithinRange } from "../../engine/hex/HexCoord";
import { effectiveStats } from "../UnitStats";
import type { Ability, AbilityContext, AbilityHit } from "./Ability";
import type { HexCoord } from "../../engine/hex/HexCoord";
import type { Unit } from "../Unit";

const RUPTURE_RADIUS = 1;
const WINDUP_MS = 1000;

function findClusterCenter(enemies: Unit[], caster: { pos: HexCoord }): HexCoord {
  let best = enemies[0];
  let bestScore = -1;
  for (const e of enemies) {
    const nearby = enemies.filter(
      other => hexDistance(e.pos, other.pos) <= RUPTURE_RADIUS + 1,
    ).length;
    // tiebreak: prefer enemy closest to caster
    const score = nearby * 100 - hexDistance(caster.pos, e.pos);
    if (score > bestScore) {
      bestScore = score;
      best = e;
    }
  }
  return best.pos;
}

export const chogathRupture: Ability = {
  id: "chogathRupture",
  displayName: "Rupture",
  description: "Marks the most clustered enemy group for 1s, then ruptures the ground for magic damage and knocking them up.",
  iconId: "ChogathRupture",

  targetShape(ctx: AbilityContext): HexCoord[] {
    const enemies = ctx.combat.livingUnitsOf(ctx.caster.team === "player" ? "enemy" : "player");
    if (enemies.length === 0) return [ctx.target.pos];
    const center = findClusterCenter(enemies, ctx.caster);
    return hexesWithinRange(center, RUPTURE_RADIUS);
  },

  computeHits(ctx: AbilityContext): AbilityHit[] {
    const enemies = ctx.combat.livingUnitsOf(ctx.caster.team === "player" ? "enemy" : "player");
    if (enemies.length === 0) return [];
    const center = findClusterCenter(enemies, ctx.caster);
    const area = hexesWithinRange(center, RUPTURE_RADIUS);
    const stats = effectiveStats(ctx.caster);
    const dmg = magicDamage(stats.ap * 0.8 + 100);

    return enemies
      .filter(e => area.some(h => h.q === e.pos.q && h.r === e.pos.r))
      .map(e => ({
        target: e,
        amount: dmg,
        type: "magical" as const,
        effect: { kind: "stun" as const, duration: 1 },
        chogathRupture: true,
        suppressPopup: false,
        delayMs: WINDUP_MS,
      }));
  },
};
